// CRM 2.0 Cancel Unit (M3-F10; rows M3.34, M3.54; DC-120 / DC-121 / DC-156 / DC-179 / DC-182).
//   request  (CRM tagged → allotment letter eSign): reason + the applicant who asks + OTP to that applicant;
//            full refund incl. stamp duty & charges; goes to your CRM, then the CRM Head
//   info     (eSign → handover): "Please call your CRM"; the CRM raises it and you confirm it by OTP
//   open     the request's status; "Bank details for your refund" when VJ asks for them (no OTP, no upload;
//            PAN and account checked on the server)
import React, { useEffect, useState } from "react";
import { Box, MenuItem, Modal, TextField } from "@mui/material";
import Close from "../../Icons/Close";
import { getCancelState, sendCancelOtp, requestCancel, submitRefundBank } from "../../../Services/Crm2Portal";
import { errorTost, successTost } from "../../../Services/Toast";
import OtpEntry from "./OtpEntry";
import useConsentPoint from "./TermsGate";
import { niceDate } from "./Crm2Parts";
import css from "./Crm2Booking.module.css";

const STAGE = {
	WAITING_CUSTOMER_OK: "Waiting for your confirmation",
	WITH_CRM: "With your CRM",
	WITH_CRM_HEAD: "With the CRM Head",
	CANCELLING_IN_FARVISION: "Approved · being cancelled",
	CANCELLED: "Cancelled · refund in progress",
	REJECTED_BY_CRM: "Rejected by your CRM",
	REJECTED_BY_CRM_HEAD: "Rejected by the CRM Head",
	REJECTED_BY_CUSTOMER: "Rejected by you",
	WITHDRAWN: "Withdrawn by VJ",
};

function RefundBankForm({ apId, onSaved }) {
	const [f, setF] = useState({ accHolderName: "", accountNo: "", ifscCode: "", bankName: "", accType: "", panNumber: "" });
	const [errs, setErrs] = useState({});
	const [busy, setBusy] = useState(false);
	const set = (k) => (e) => setF({ ...f, [k]: ["ifscCode", "panNumber"].includes(k) ? e.target.value.toUpperCase() : k === "accountNo" ? e.target.value.replace(/\D/g, "") : e.target.value });
	const save = () => {
		setBusy(true);
		submitRefundBank(apId, f)
			.then((r) => {
				if (r.status === "VERIFIED") successTost("Bank details verified. Accounts will pay your refund to this account.");
				else if (r.status === "FAILED") errorTost(r.note || "Bank details not matched. Please check and try again.");
				else successTost("Bank details received.");
				onSaved();
			})
			.catch((err) => {
				setErrs(err.fieldErrors || {});
				errorTost(err.message);
			})
			.finally(() => setBusy(false));
	};
	const fld = (k, label, extra = {}) => <TextField key={k} label={label} value={f[k]} onChange={set(k)} error={!!errs[k]} helperText={errs[k] || ""} size="small" fullWidth {...extra} />;
	return (
		<div className={css.bankBlock}>
			<div className={css.bankHead}>
				<b>Bank details for your refund</b>
				<span>Your online refund could not be paid back. Give the bank account for your refund; the PAN name must match the account holder.</span>
			</div>
			<div className={css.bankGrid}>
				{fld("accHolderName", "Account holder name")}
				{fld("accountNo", "Account number", { inputProps: { inputMode: "numeric", maxLength: 18 } })}
				{fld("ifscCode", "IFSC", { inputProps: { maxLength: 11 } })}
				{fld("bankName", "Bank name")}
				{fld("accType", "Account type", { select: true, children: ["Savings", "Current", "NRE", "NRO"].map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>) })}
				{fld("panNumber", "PAN", { inputProps: { maxLength: 10 } })}
			</div>
			<div className={css.otpActs}>
				<button type="button" className={`${css.btn} ${css.btnP}`} disabled={busy} onClick={save}>
					{busy ? "Checking…" : "Submit bank details"}
				</button>
			</div>
		</div>
	);
}

export default function CancelUnitModal({ allotment, card, onClose, onDone }) {
	const apId = allotment.allotmentPaymentId;
	const [st, setSt] = useState(null);
	const [reason, setReason] = useState("");
	const [applicantId, setApplicantId] = useState("");
	const [termsOk, setTermsOk] = useState(false);
	const { requirePoint, termsModal } = useConsentPoint();
	const load = () =>
		getCancelState(apId)
			.then(setSt)
			.catch((err) => errorTost(err.message));
	useEffect(() => {
		load();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [apId]);
	useEffect(() => {
		if (st?.window === "request") requirePoint("cancel_request", apId, () => setTermsOk(true));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [st?.window]);

	if (termsModal) return termsModal;
	const needApplicant = (st?.applicants || []).length > 0;
	return (
		<Modal open onClose={(_, r) => r !== "backdropClick" && onClose()} sx={{ ".MuiBackdrop-root": { background: "rgba(52, 64, 84, 0.7)" } }} disableAutoFocus>
			<Box className={css.modal}>
				<div className={css.modalHeader}>
					<span>Cancel Unit</span>
					<span className={css.modalClose} onClick={onClose} tabIndex={0}>
						<Close width={"1.2rem"} height={"1.2rem"} />
					</span>
				</div>
				<div className={css.modalContent}>
					{!st && <p className={css.muted}>Loading…</p>}
					{st?.request && st.window === "open" && (
						<div className={css.info}>
							<b>Cancellation requested {niceDate(st.request.at)}</b> · {STAGE[st.request.stage] || st.request.status}
							{st.request.reason ? ` · "${st.request.reason}"` : ""}
						</div>
					)}
					{st?.refundBankNeeded && <RefundBankForm apId={apId} onSaved={load} />}
					{st?.request?.refundBankStatus === "VERIFIED" && <div className={css.good}>Bank details verified. Accounts pays your refund to this account.</div>}
					{st?.request?.refundBankStatus === "FAILED" && !st.refundBankNeeded && <div className={css.badBox}>Bank details not matched: {st.request.refundBankNote}</div>}
					{st && !st.window && !st.request && <p className={css.muted}>Cancel Unit is available after your Relationship Manager is assigned.</p>}
					{st?.window === "info" && (
						<div className={css.info}>
							Your allotment letter is signed. To cancel, please call your CRM{card?.crm ? ` ${card.crm.name}${card.crm.phone ? ` · ${card.crm.phone}` : ""}` : ""}. Your CRM raises the cancellation and you confirm it by OTP.
							{st.afterGrnNote ? " Stamp duty already paid is not refunded by VJ (you may claim it from IGR)." : ""}
						</div>
					)}
					{st?.window === "request" && (
						<>
							<p className={css.muted}>Full refund of what you paid, including stamp duty &amp; charges. Your request goes to your CRM, then the CRM Head.</p>
							<TextField label="Why do you want to cancel?" value={reason} onChange={(e) => setReason(e.target.value)} multiline minRows={2} fullWidth size="small" />
							{needApplicant && (
								<TextField select label="Applicant asking to cancel" value={applicantId} onChange={(e) => setApplicantId(e.target.value)} fullWidth size="small">
									{st.applicants.map((a) => (
										<MenuItem key={a.applicantId} value={a.applicantId}>
											{a.no}. {a.name} · {a.maskedMobile}
										</MenuItem>
									))}
								</TextField>
							)}
							<OtpEntry
								disabled={!termsOk || reason.trim().length < 5 || (needApplicant && !applicantId)}
								sendLabel="Send OTP"
								confirmLabel="Request cancellation"
								send={() => sendCancelOtp(apId, applicantId || null)}
								verify={(challengeId, code) => requestCancel(apId, { reason, applicantId: applicantId || null, challengeId, code })}
								onVerified={() => {
									successTost("Cancellation requested. Your CRM will contact you.");
									onDone();
								}}
							/>
						</>
					)}
				</div>
			</Box>
		</Modal>
	);
}
